import React from "react";
import { Badge, Code, CodeBlock, Note, Section, Table, Tbody, Thead, MONO, TD } from "./primitives";

const TOKEN_SAMPLE = `import requests

tenant_id = "09bb5b3b-dfce-473f-9615-47efe8193e80"
token_url = f"https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/token"

token_data = {
    "grant_type": "client_credentials",
    "client_id": "your_client_id",
    "client_secret": "your_client_secret",
    # use api://api-betm-com/.default for production
    "scope": "api://stage-api-betm-com/.default",
}
token_response = requests.post(token_url, data=token_data)
token_response.raise_for_status()
access_token = token_response.json()["access_token"]

headers = {
    "Authorization": f"Bearer {access_token}",
    "Content-Type": "application/json",
    "Product": "EXT",
    "Ocp-Apim-Subscription-Key": "your_subscription_key",
}`;

const AuthenticationDoc: React.FC = () => (
  <div className="space-y-8">
    <p className="text-sm leading-6 text-gray-700">
      Every BETM API is published through the same Azure APIM gateway and shares the same OAuth
      resource per environment. Each service is exposed under its own path prefix and its own APIM
      product subscription key. Read this page once, then go to the service you need.
    </p>

    <Section title="Environments">
      <Table minWidth={680}>
        <Thead cols={["Environment", "Purpose", "Gateway Base URL", "OAuth Scope"]} />
        <Tbody>
          <tr>
            <td className={`${TD} font-medium`}>Production</td>
            <td className={TD}>Live production data access</td>
            <td className={`${TD} ${MONO}`}>https://api.betm.com/</td>
            <td className={`${TD} ${MONO}`}>api://api-betm-com/.default</td>
          </tr>
          <tr>
            <td className={`${TD} font-medium`}>Stage</td>
            <td className={TD}>Pre-production testing and validation</td>
            <td className={`${TD} ${MONO}`}>https://api-stg.betm.com/</td>
            <td className={`${TD} ${MONO}`}>api://stage-api-betm-com/.default</td>
          </tr>
        </Tbody>
      </Table>
    </Section>

    <Section title="Required headers">
      <Table minWidth={720}>
        <Thead cols={["Header", "Required", "Notes"]} />
        <Tbody>
          <tr>
            <td className={`${TD} ${MONO}`}>Authorization</td>
            <td className={TD}>Yes</td>
            <td className={TD}>
              <Code>Bearer &lt;access_token&gt;</Code> from the client-credentials flow below.
            </td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>Ocp-Apim-Subscription-Key</td>
            <td className={TD}>Yes</td>
            <td className={TD}>
              Subscription key for that service&apos;s APIM product. It differs per service and per
              environment. Request yours from the platform team; never share it or commit it.
            </td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>Product</td>
            <td className={TD}>Recommended</td>
            <td className={TD}>
              Selects which JWT audience and issuer the gateway validates. Exactly one of{" "}
              <Code>EXT</Code>, <Code>APIM</Code>, <Code>UI</Code>, <Code>AMP-UI</Code>,{" "}
              <Code>Mobile-App</Code>. External and partner integrations use <Code>EXT</Code>.
            </td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>Content-Type</td>
            <td className={TD}>POST/PUT</td>
            <td className={TD}>
              <Code>application/json</Code>.
            </td>
          </tr>
        </Tbody>
      </Table>
      <Note>
        <b>
          <Code>Product</Code> values are case-sensitive.
        </b>{" "}
        The gateway compares the header with an exact string match, so <Code>mobile-app</Code> does
        not match <Code>Mobile-App</Code>. A missing or unrecognised value silently falls through to
        the <Code>EXT</Code> audience and issuer, so a token minted for a different product fails
        with a bare <Code>401 Unauthorized</Code> rather than a clear &quot;unknown Product&quot;
        error.
      </Note>
    </Section>

    <Section title="Azure AD authentication">
      <p className="text-sm leading-6 text-gray-700">
        Client-credentials integrations (<Code>Product: EXT</Code>) authenticate against tenant{" "}
        <Code>09bb5b3b-dfce-473f-9615-47efe8193e80</Code>. Request a token, then send it on every
        call.
      </p>
      <CodeBlock code={TOKEN_SAMPLE} label="Python - get a token and build the request headers" />
      <Note tone="info">
        This tenant is not universal across every <Code>Product</Code>. The gateway validates a
        different issuer per value: <Code>AMP-UI</Code> and <Code>Mobile-App</Code> tokens are issued
        by the BETM external (CIAM) tenant, and <Code>APIM</Code> and <Code>UI</Code> tokens by the
        BETM corporate tenant. Only <Code>EXT</Code>, and the no-header fallback, validates against
        the tenant above. Match your issuer to the <Code>Product</Code> header you send.
      </Note>
    </Section>

    <Section title="Reading the availability badges">
      <p className="text-sm leading-6 text-gray-700">
        Code being deployed is not the same as a route being callable. Each endpoint must be
        registered as an APIM operation, and where that has not happened the gateway answers before
        the request ever reaches the service. Every endpoint table in this documentation is tagged:
      </p>
      <ul className="space-y-2 text-sm text-gray-700">
        <li className="flex items-start gap-3">
          <Badge tone="prod">Prod &amp; Stage</Badge>
          <span>Callable in both environments.</span>
        </li>
        <li className="flex items-start gap-3">
          <Badge tone="stage">Stage only</Badge>
          <span>
            Callable in Stage; Production returns <Code>404</Code> until the gateway import runs.
          </span>
        </li>
        <li className="flex items-start gap-3">
          <Badge tone="gap">Not published</Badge>
          <span>
            Exists in the service but has no gateway operation, so it returns <Code>404</Code>{" "}
            everywhere.
          </span>
        </li>
      </ul>
      <Table minWidth={560}>
        <Thead cols={["Response", "What it means"]} />
        <Tbody>
          <tr>
            <td className={`${TD} ${MONO}`}>401 Access Denied</td>
            <td className={TD}>
              The operation exists and your credentials were rejected. Check the token, the{" "}
              <Code>Product</Code> header and the subscription key.
            </td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>403 Forbidden</td>
            <td className={TD}>
              Authenticated, but not entitled to the requested asset. Ask for entitlement rather than
              retrying.
            </td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>404 Resource Not Found</td>
            <td className={TD}>
              No gateway operation matched the path and method. This is a publishing gap, not a
              mistake in your request. Contact the platform team.
            </td>
          </tr>
        </Tbody>
      </Table>
    </Section>
  </div>
);

export default AuthenticationDoc;
